import type { ReactNode, Ref } from "react";
import { FileDiffIcon, LayoutListIcon } from "lucide-react";
import { AnimatedReviewHeader } from "./finding/AnimatedReviewHeader";
import { AnimatedTabsList, AnimatedTabsTrigger } from "../ui/animated-tabs";
import { REVIEW_DETAIL_COLUMNS } from "./ReviewDetail";

/** Align the expanded back icon with the title in both review surfaces. */
export const REVIEW_BACK_TRANSFORM =
  "translateX(calc((0.5 * var(--review-header-inset) - 8px) * (1 - var(--header-progress)) - 4px * var(--header-progress)))";

/** The finding header and composer placement, shared by both kinds of review. */
export function ReviewPageHeader({
  kind,
  title,
  description,
  id,
  back,
  status,
  actions,
}: {
  kind: "finding" | "suggestion";
  title: string;
  description: string;
  id: string;
  back: ReactNode;
  status: ReactNode;
  actions: ReactNode;
}) {
  return (
    <AnimatedReviewHeader
      expanded={
        <div className="space-y-1">
          <span
            className="block truncate font-heading text-xl font-semibold leading-6"
            title={title}
          >
            {title}
          </span>
          <p
            data-finding-subtitle={kind === "finding" ? true : undefined}
            className="truncate text-sm text-muted-foreground"
            title={description}
          >
            {description}
          </p>
        </div>
      }
      compact={
        <>
          {back}
          <span
            data-finding-identifier={kind === "finding" ? true : undefined}
            className="shrink-0 whitespace-nowrap font-mono text-xs text-foreground"
            style={{ opacity: "var(--header-expanded-opacity)" }}
            title={id}
          >
            {/* The short id, as Details shows it. The whole UUID outran the status
                chips, which slide left over it as the header collapses. */}
            #{id.slice(0, 8)}
          </span>
        </>
      }
      status={status}
      tabs={
        <AnimatedTabsList
          noBorder
          aria-label={kind === "finding" ? "Finding review" : "Suggestion review"}
        >
          <AnimatedTabsTrigger value="overview">
            <LayoutListIcon aria-hidden className="size-4" />
            Overview
          </AnimatedTabsTrigger>
          <AnimatedTabsTrigger value="changes">
            <FileDiffIcon aria-hidden className="size-4" />
            Changes
          </AnimatedTabsTrigger>
        </AnimatedTabsList>
      }
      actions={actions}
    />
  );
}

export function ReviewComposerDock({
  tab,
  hostRef,
}: {
  tab: string;
  hostRef: Ref<HTMLDivElement>;
}) {
  return (
    <div className="px-5 pb-4 pt-2 sm:px-8">
      <div className={tab === "overview" ? REVIEW_DETAIL_COLUMNS : "mx-auto w-full max-w-3xl"}>
        <div className="pointer-events-auto min-w-0" ref={hostRef} />
      </div>
    </div>
  );
}
